import { create } from "zustand";
import type { JobStatus, ProgressStats } from "@tg/shared";

export interface LogEntry {
  ts: number;
  title: string;
  result: "sent" | "skipped" | "failed" | "deferred" | "info";
  detail?: string;
}

export type BannerKind = "flood" | "slowmode" | "peerflood" | "pause" | "window" | "cap" | "auth";
export interface JobBanner {
  kind: BannerKind;
  text: string;
  until?: number | null;
}

interface JobState {
  status: JobStatus | "idle";
  jobId: number | null;
  total: number;
  sent: number;
  skipped: number;
  failed: number;
  pending: number;
  deferred: number;
  todaySent: number;
  dailyCap: number;
  cooldownUntil: number | null;
  current: { groupId: string; title: string; nextDelayMs?: number } | null;
  banner: JobBanner | null;
  log: LogEntry[];

  reset: () => void;
  setStatus: (s: JobState["status"]) => void;
  setJobId: (id: number | null) => void;
  applyProgress: (p: ProgressStats) => void;
  setCurrent: (c: JobState["current"]) => void;
  setBanner: (b: JobBanner | null) => void;
  pushLog: (e: LogEntry) => void;
}

const MAX_LOG = 500;

export const useJobStore = create<JobState>((set) => ({
  status: "idle",
  jobId: null,
  total: 0,
  sent: 0,
  skipped: 0,
  failed: 0,
  pending: 0,
  deferred: 0,
  todaySent: 0,
  dailyCap: 0,
  cooldownUntil: null,
  current: null,
  banner: null,
  log: [],

  reset: () =>
    set({
      status: "idle",
      jobId: null,
      total: 0,
      sent: 0,
      skipped: 0,
      failed: 0,
      pending: 0,
      deferred: 0,
      todaySent: 0,
      dailyCap: 0,
      cooldownUntil: null,
      current: null,
      banner: null,
      log: [],
    }),
  setStatus: (status) => set({ status }),
  setJobId: (jobId) => set({ jobId }),
  applyProgress: (p) =>
    set({
      status: p.status,
      total: p.total,
      sent: p.sent,
      skipped: p.skipped,
      failed: p.failed,
      pending: p.pending,
      deferred: p.deferred,
      todaySent: p.todaySent,
      dailyCap: p.dailyCap,
      cooldownUntil: p.cooldownUntil,
    }),
  setCurrent: (current) => set({ current }),
  setBanner: (banner) => set({ banner }),
  pushLog: (e) =>
    set((s) => ({ log: [e, ...s.log].slice(0, MAX_LOG) })),
}));
